import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const stats = [
  { label: '全球节点', value: '128,000+' },
  { label: '活跃用户', value: '3,200万+' },
  { label: '生态伙伴', value: '500+' },
  { label: '网络速度', value: '1Tbps' },
];

const ticker = [
  '6G商用元年 · 元宇宙爆发',
  'Web5.0协议正式发布',
  'META66生态节点突破10万',
  '数字孪生城市项目启动',
  '量子加密通信全面部署',
  'XR设备出货量突破1亿台',
  '虚拟经济总量超越实体GDP',
  'META人物志 · 先行者名人堂正式上线',
];

const sections = [
  { id: 'meta-vision', icon: 'ri-earth-line', label: '元宇宙愿景', color: '#00FF88', desc: '人类使命' },
  { id: 'network', icon: 'ri-cpu-line', label: '核心技术', color: '#FFD700', desc: '技术矩阵' },
  { id: 'xr', icon: 'ri-glasses-2-line', label: 'XR体验', color: '#FF6B9D', desc: '沉浸未来' },
  { id: 'mars-city', icon: 'ri-planet-line', label: '火星城市', color: '#FF6B35', desc: '2047实况' },
  { id: 'digital-assets', icon: 'ri-fingerprint-line', label: '数字资产', color: '#00FFCC', desc: '无证件时代' },
  { id: 'future-tech', icon: 'ri-brain-line', label: '前沿科技', color: '#A855F7', desc: '连载故事' },
  { id: 'nexus-robot', icon: 'ri-robot-line', label: 'NEXUS-1', color: '#FF6B9D', desc: '超维机器人' },
  { id: 'news-timeline', icon: 'ri-newspaper-line', label: '时讯', color: '#00FF88', desc: '实时动态' },
  { id: 'bbs', icon: 'ri-discuss-line', label: 'BBS论坛', color: '#FFD700', desc: '社区' },
  { id: 'future-vision', icon: 'ri-rocket-line', label: '未来展望', color: '#00FFCC', desc: '2030-2050' },
  { id: 'meta-shop', icon: 'ri-store-3-line', label: '超级商城', color: '#FFD700', desc: '机器人商城' },
  { id: 'meta-pioneers', icon: 'ri-user-star-line', label: 'META人物志', color: '#FF6B35', desc: '先行者名人堂' },
];

const rainbowColors = ['#00FF88', '#FFD700', '#FF6B9D', '#00FFCC', '#FF6B35', '#A855F7', '#00FF88', '#FFD700', '#FF6B9D', '#00FFCC'];

export default function Hero({ lang: _lang = 'zh' }: { lang?: 'zh' | 'en' }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const navigate = useNavigate();
  const [tickerIdx, setTickerIdx] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [hoveredSection, setHoveredSection] = useState<string | null>(null);
  const [litSectionIdx, setLitSectionIdx] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTickerIdx((i) => (i + 1) % ticker.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Rainbow piano for section nav
  useEffect(() => {
    const interval = setInterval(() => {
      setLitSectionIdx((i) => (i + 1) % sections.length);
    }, 600);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Enhanced particles with more types
    const particles: {
      x: number; y: number; vx: number; vy: number;
      size: number; alpha: number; color: string;
      type: 'dot' | 'star' | 'ring'; pulse: number; pulseSpeed: number;
    }[] = [];

    const colors = [
      'rgba(0,255,136,', 'rgba(255,215,0,', 'rgba(255,107,157,',
      'rgba(0,255,204,', 'rgba(255,107,53,', 'rgba(168,85,247,',
    ];

    for (let i = 0; i < 140; i++) {
      const type = i < 100 ? 'dot' : i < 130 ? 'star' : 'ring';
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: type === 'ring' ? Math.random() * 4 + 2 : Math.random() * 2.5 + 0.5,
        alpha: Math.random() * 0.6 + 0.1,
        color: colors[Math.floor(Math.random() * colors.length)],
        type,
        pulse: Math.random() * Math.PI * 2,
        pulseSpeed: 0.02 + Math.random() * 0.03,
      });
    }

    // Shooting stars
    const shootingStars: { x: number; y: number; vx: number; vy: number; len: number; alpha: number; color: string }[] = [];
    const addShootingStar = () => {
      if (shootingStars.length < 3) {
        const color = colors[Math.floor(Math.random() * colors.length)];
        shootingStars.push({
          x: Math.random() * canvas.width * 0.7,
          y: Math.random() * canvas.height * 0.5,
          vx: 4 + Math.random() * 4,
          vy: 1 + Math.random() * 2,
          len: 80 + Math.random() * 80,
          alpha: 0.8,
          color,
        });
      }
    };
    const shootInterval = setInterval(addShootingStar, 2500);

    let animId: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw particles
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.pulse += p.pulseSpeed;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        const pulsedAlpha = p.alpha * (0.7 + 0.3 * Math.sin(p.pulse));
        const pulsedSize = p.size * (0.9 + 0.1 * Math.sin(p.pulse));

        if (p.type === 'ring') {
          ctx.beginPath();
          ctx.arc(p.x, p.y, pulsedSize, 0, Math.PI * 2);
          ctx.strokeStyle = `${p.color}${pulsedAlpha})`;
          ctx.lineWidth = 0.8;
          ctx.stroke();
        } else if (p.type === 'star') {
          // Draw 4-point star
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.pulse * 0.5);
          ctx.beginPath();
          for (let j = 0; j < 4; j++) {
            const angle = (j * Math.PI) / 2;
            const r = pulsedSize * 2;
            ctx.lineTo(Math.cos(angle) * r, Math.sin(angle) * r);
            ctx.lineTo(Math.cos(angle + Math.PI / 4) * (r * 0.3), Math.sin(angle + Math.PI / 4) * (r * 0.3));
          }
          ctx.closePath();
          ctx.fillStyle = `${p.color}${pulsedAlpha * 0.8})`;
          ctx.fill();
          ctx.restore();
        } else {
          ctx.beginPath();
          ctx.arc(p.x, p.y, pulsedSize, 0, Math.PI * 2);
          ctx.fillStyle = `${p.color}${pulsedAlpha})`;
          ctx.fill();
        }
      });

      // Draw connections
      particles.slice(0, 100).forEach((p, i) => {
        particles.slice(i + 1, i + 8).forEach((q) => {
          const dist = Math.hypot(p.x - q.x, p.y - q.y);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            const alpha = 0.08 * (1 - dist / 120);
            ctx.strokeStyle = `rgba(0,255,136,${alpha})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });

      // Draw shooting stars
      for (let i = shootingStars.length - 1; i >= 0; i--) {
        const s = shootingStars[i];
        s.x += s.vx;
        s.y += s.vy;
        s.alpha -= 0.012;
        if (s.alpha <= 0 || s.x > canvas.width + 100) {
          shootingStars.splice(i, 1);
          continue;
        }
        const grad = ctx.createLinearGradient(s.x - s.len, s.y - s.vy * (s.len / s.vx), s.x, s.y);
        grad.addColorStop(0, `${s.color}0)`);
        grad.addColorStop(1, `${s.color}${s.alpha})`);
        ctx.beginPath();
        ctx.moveTo(s.x - s.len, s.y - s.vy * (s.len / s.vx));
        ctx.lineTo(s.x, s.y);
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.5;
        ctx.stroke();
        // Star head glow
        ctx.beginPath();
        ctx.arc(s.x, s.y, 2, 0, Math.PI * 2);
        ctx.fillStyle = `${s.color}${s.alpha})`;
        ctx.fill();
      }

      animId = requestAnimationFrame(animate);
    };
    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(animId);
      clearInterval(shootInterval);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const scrollTo = (id: string) => {
    if (id === 'meta-shop') {
      navigate('/shop');
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="hero" className="relative min-h-screen flex flex-col overflow-hidden bg-[#020810]">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/3a4a1bb6c9ede05450e34ee3b20e35ab.jpg"
          alt="META66 元宇宙背景"
          className="w-full h-full object-cover object-center opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#020810]/75 via-[#020810]/20 to-[#020810]/95" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#020810]/80 via-transparent to-[#020810]/60" />
      </div>

      {/* Doodle decorations */}
      <div className="absolute top-32 right-20 w-48 h-48 rounded-full opacity-10 pointer-events-none" style={{ background: 'radial-gradient(ellipse, rgba(255,215,0,0.6) 0%, transparent 70%)' }} />
      <div className="absolute bottom-40 left-16 w-32 h-32 rounded-full opacity-10 pointer-events-none" style={{ background: 'radial-gradient(ellipse, rgba(255,107,157,0.6) 0%, transparent 70%)' }} />

      {/* Particle Canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" />

      {/* Doodle grid */}
      <div className="absolute inset-0 pointer-events-none doodle-grid-bg opacity-50" />

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex flex-col justify-center px-6 md:px-16 lg:px-24 pt-24 pb-8">
        <div className="max-w-[1400px] mx-auto w-full">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            {/* Left Content */}
            <div className="flex-1 flex flex-col gap-6 lg:gap-8">
              <div className="flex items-center gap-3">
                <div className="section-badge">
                  <span className="w-2 h-2 rounded-full bg-[#00FF88] animate-pulse" />
                  Next-Gen Metaverse Platform
                </div>
              </div>

              <div>
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-black leading-tight text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                  <span className="block">META</span>
                  <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#00FF88] via-[#FFD700] to-[#FF6B9D]">
                    VERSE 66
                  </span>
                  <span className="block text-3xl md:text-4xl lg:text-5xl font-bold text-white/90 mt-2">
                    {_lang === 'en' ? 'Metaverse Ecosystem' : '元宇宙生态系统'}
                  </span>
                </h1>
              </div>

              <p className="text-white/60 text-base md:text-lg leading-relaxed max-w-xl">
                {_lang === 'en'
                  ? <>Bridging the <span className="text-[#00FF88] font-semibold">6G Ultra-Speed</span> Network Era · Building a <span className="text-[#FFD700] font-semibold">Web5.0</span> Decentralized Future · Pioneering the <span className="text-[#FF6B9D] font-semibold">Digital Civilization</span> Renaissance</>
                  : <>连接<span className="text-[#00FF88] font-semibold">6G</span>超高速网络时代 · 构建<span className="text-[#FFD700] font-semibold">Web5.0</span>去中心化未来 · 开启<span className="text-[#FF6B9D] font-semibold">数字文明</span>新纪元</>
                }
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#metaverse"
                  onClick={(e) => { e.preventDefault(); scrollTo('metaverse'); }}
                  className="group flex items-center justify-center gap-3 px-8 py-4 text-black font-bold text-base hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer btn-doodle"
                  style={{ backgroundColor: '#00FF88' }}
                >
                  <span>{_lang === 'en' ? 'Explore Metaverse' : '探索元宇宙'}</span>
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-black/20 group-hover:translate-x-1 transition-transform duration-300">
                    <i className="ri-arrow-right-line text-sm" />
                  </div>
                </a>
                <button
                  onClick={() => setShowVideo(true)}
                  className="group flex items-center justify-center gap-3 px-8 py-4 border border-white/20 text-white font-semibold text-base hover:border-[#00FF88]/60 hover:text-[#00FF88] transition-all duration-300 whitespace-nowrap cursor-pointer btn-doodle"
                >
                  <div className="w-6 h-6 flex items-center justify-center rounded-full border border-current">
                    <i className="ri-play-fill text-xs" />
                  </div>
                  <span>{_lang === 'en' ? 'Watch Demo' : '观看演示视频'}</span>
                </button>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-2">
                {stats.map((s, i) => (
                  <div key={s.label} className="flex flex-col gap-1">
                    <span className="text-xl md:text-2xl font-black" style={{ fontFamily: 'Orbitron, sans-serif', color: rainbowColors[i] }}>{s.value}</span>
                    <span className="text-white/40 text-xs tracking-wider">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Visual */}
            <div className="flex-1 flex items-center justify-center relative">
              <div className="relative w-full max-w-[480px] aspect-square">
                <div className="absolute inset-0 rounded-full border-2 border-[#00FF88]/15 animate-spin" style={{ animationDuration: '20s', borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%' }} />
                <div className="absolute inset-4 rounded-full border border-[#FFD700]/10 animate-spin" style={{ animationDuration: '15s', animationDirection: 'reverse', borderRadius: '45% 55% 50% 50% / 55% 45% 50% 50%' }} />
                <div className="absolute inset-8 rounded-full border border-[#FF6B9D]/10 animate-spin" style={{ animationDuration: '10s', borderRadius: '55% 45% 50% 50% / 45% 55% 50% 50%' }} />

                <div className="absolute inset-16 overflow-hidden border-2 border-[#00FF88]/30" style={{ borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%', boxShadow: '0 0 60px rgba(0,255,136,0.25), inset 0 0 40px rgba(0,255,136,0.08)' }}>
                  <img
                    src="https://readdy.ai/api/search-image?query=holographic%20earth%20globe%20glowing%20colorful%20neon%20digital%20network%20connections%20nodes%20data%20streams%20dark%20background%20futuristic%20technology%203D%20render%20doodle%20art%20style%20vibrant&width=400&height=400&seq=globe-doodle-001&orientation=squarish"
                    alt="元宇宙地球"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#00FF88]/10" />
                </div>

                {[
                  { icon: 'ri-signal-tower-line', label: '6G网络', top: '-8px', left: '50%', transform: 'translateX(-50%)', color: '#00FF88' },
                  { icon: 'ri-glasses-line', label: 'XR体验', top: '25%', right: '-8px', color: '#FFD700' },
                  { icon: 'ri-global-line', label: 'Web5.0', bottom: '25%', right: '-8px', color: '#FF6B9D' },
                  { icon: 'ri-coin-line', label: '数字经济', bottom: '-8px', left: '50%', transform: 'translateX(-50%)', color: '#00FFCC' },
                  { icon: 'ri-brain-line', label: 'AI智能体', bottom: '25%', left: '-8px', color: '#FFD700' },
                  { icon: 'ri-shield-keyhole-line', label: '量子安全', top: '25%', left: '-8px', color: '#00FF88' },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="absolute flex flex-col items-center gap-1 px-2.5 py-2 bg-black/70 backdrop-blur-sm border transition-all duration-300 cursor-pointer"
                    style={{
                      top: item.top,
                      bottom: item.bottom,
                      left: item.left,
                      right: item.right,
                      transform: item.transform,
                      borderColor: `${item.color}40`,
                      borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                      boxShadow: `0 0 12px ${item.color}20`,
                    }}
                    onClick={() => scrollTo('metaverse')}
                  >
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${item.icon} text-sm`} style={{ color: item.color }} />
                    </div>
                    <span className="text-white/70 text-xs whitespace-nowrap">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Rainbow Piano Section Nav */}
      <div className="relative z-10 border-t border-white/8 bg-black/50 backdrop-blur-md px-6 py-5">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-1 h-4 rounded-full bg-[#00FF88]" />
            <span className="text-white/40 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: 'Caveat, cursive' }}>
              全站板块导览 · Site Navigation
            </span>
            <div className="flex items-center gap-1 ml-2">
              {sections.map((_, i) => (
                <div
                  key={i}
                  className="w-1.5 h-1.5 rounded-full transition-all duration-300"
                  style={{
                    backgroundColor: litSectionIdx === i ? sections[i].color : 'rgba(255,255,255,0.15)',
                    transform: litSectionIdx === i ? 'scale(1.5)' : 'scale(1)',
                    boxShadow: litSectionIdx === i ? `0 0 6px ${sections[i].color}` : 'none',
                  }}
                />
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-10 gap-2">
            {sections.map((s, idx) => {
              const isLit = litSectionIdx === idx;
              const isHovered = hoveredSection === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  onMouseEnter={() => setHoveredSection(s.id)}
                  onMouseLeave={() => setHoveredSection(null)}
                  className="group flex flex-col items-center gap-1.5 p-3 transition-all duration-300 cursor-pointer relative overflow-hidden"
                  style={{
                    borderRadius: '10px 8px 12px 8px / 8px 12px 8px 10px',
                    border: isLit || isHovered ? `1.5px solid ${s.color}60` : '1px solid rgba(255,255,255,0.08)',
                    background: isLit ? `${s.color}12` : isHovered ? `${s.color}08` : 'rgba(255,255,255,0.02)',
                    transform: isLit ? 'translateY(-3px) scale(1.05)' : isHovered ? 'translateY(-2px)' : 'none',
                    boxShadow: isLit ? `0 0 20px ${s.color}30, 0 4px 12px ${s.color}15` : 'none',
                  }}
                >
                  {/* Piano key bottom glow */}
                  {isLit && (
                    <div
                      className="absolute bottom-0 left-0 right-0 h-0.5"
                      style={{ background: s.color, boxShadow: `0 0 8px ${s.color}` }}
                    />
                  )}
                  <div
                    className="w-8 h-8 flex items-center justify-center transition-all duration-300"
                    style={{
                      backgroundColor: isLit || isHovered ? `${s.color}20` : 'rgba(255,255,255,0.05)',
                      borderRadius: '50% 45% 55% 50% / 50% 55% 45% 50%',
                    }}
                  >
                    <i className={`${s.icon} text-sm transition-colors duration-300`} style={{ color: isLit || isHovered ? s.color : 'rgba(255,255,255,0.4)' }} />
                  </div>
                  <span
                    className="text-xs font-medium text-center leading-tight whitespace-nowrap transition-colors duration-300"
                    style={{ color: isLit ? s.color : isHovered ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.5)' }}
                  >
                    {s.label}
                  </span>
                  <span
                    className="text-xs text-center transition-colors duration-300"
                    style={{ fontFamily: 'Caveat, cursive', fontSize: '10px', color: isLit ? `${s.color}90` : 'rgba(255,255,255,0.2)' }}
                  >
                    {s.desc}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Ticker Bar */}
      <div className="relative z-10 border-t border-[#00FF88]/15 bg-black/40 backdrop-blur-sm px-6 py-2.5">
        <div className="max-w-[1400px] mx-auto flex items-center gap-4">
          <span className="text-[#00FF88] text-xs font-bold tracking-widest uppercase whitespace-nowrap" style={{ fontFamily: 'Caveat, cursive' }}>LIVE</span>
          <div className="w-px h-4 bg-[#00FF88]/30" />
          <div className="overflow-hidden flex-1">
            <p className="text-white/60 text-sm transition-all duration-500 whitespace-nowrap">
              {ticker[tickerIdx]}
            </p>
          </div>
          <div className="flex items-center gap-4 ml-auto">
            {['META', 'WEB4', '6G', 'XR'].map((tag) => (
              <span key={tag} className="text-[#00FF88]/50 text-xs font-mono whitespace-nowrap hidden sm:block" style={{ fontFamily: 'Caveat, cursive' }}>
                #{tag}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideo && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowVideo(false)}
        >
          <div
            className="relative w-full max-w-4xl aspect-video bg-black rounded-2xl overflow-hidden border border-[#00FF88]/30"
            onClick={(e) => e.stopPropagation()}
          >
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/eqR_IHqFMiI?autoplay=1&rel=0&modestbranding=1"
              title="META66 元宇宙演示视频"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
            <button
              onClick={() => setShowVideo(false)}
              className="absolute top-4 right-4 z-20 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
